import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, UserPlus, Mail, Loader2, Inbox, Trash2, Hourglass, MoreVertical } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { InviteRevealDialog } from "@/components/users/InviteRevealDialog";

type MemberResponse = components["schemas"]["MemberResponse"];
type ClientInviteResponse = components["schemas"]["ClientInviteResponse"];
type ClientInviteWithTokenResponse = components["schemas"]["ClientInviteWithTokenResponse"];
type ClientRole = MemberResponse["role"];

const dateFmt = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const ASSIGNABLE_ROLES: ClientRole[] = ["Admin", "Developer", "Member"];

const ROLE_OPTIONS: { value: ClientRole; description: string }[] = [
  { value: "Admin",     description: "Manages team, keys, webhooks, settings" },
  { value: "Developer", description: "Manages keys + webhooks only" },
  { value: "Member",    description: "Everyday access, no keys or settings" },
];

function roleStyles(r: ClientRole) {
  switch (r) {
    case "Owner":     return "border-secondary/50 bg-secondary/15 text-secondary-foreground";
    default:          return "border-border bg-muted";
  }
}

export function UsersPage() {
  const { activeClient, activeClientId, user } = useAuth();
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState<MemberResponse[] | null>(null);
  const [invites, setInvites] = useState<ClientInviteResponse[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [revealedInvite, setRevealedInvite] = useState<ClientInviteWithTokenResponse | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      api.GET("/v1/client/members"),
      api.GET("/v1/client/invites"),
    ])
      .then(([m, i]) => {
        if (cancelled) return;
        if (m.error || i.error) { setError("Couldn't load members or invites."); return; }
        setMembers(m.data ?? []);
        setInvites(i.data ?? []);
      })
      .catch(() => { if (!cancelled) setError("Network error."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId, tick]);

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    if (!query) return members;
    const q = query.toLowerCase();
    return members.filter((m) =>
      m.email.toLowerCase().includes(q) ||
      (m.name ?? "").toLowerCase().includes(q)
    );
  }, [members, query]);

  const filteredInvites = useMemo(() => {
    if (!invites) return [];
    if (!query) return invites;
    const q = query.toLowerCase();
    return invites.filter((i) => i.email.toLowerCase().includes(q));
  }, [invites, query]);

  const counts = members && invites
    ? { active: members.length, pending: invites.length, roles: new Set(members.map((m) => m.role)).size }
    : null;

  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Members</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {counts
              ? `${counts.active} active · ${counts.pending} pending · ${counts.roles} role${counts.roles === 1 ? "" : "s"}`
              : "Loading…"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find by name / email…"
              className="h-9 w-60 pl-8"
            />
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={!isAdmin || !activeClientId}>
                <UserPlus className="size-3.5" /> Invite member
              </Button>
            </DialogTrigger>
            <InviteDialog
              clientId={activeClientId!}
              onClose={() => setInviteOpen(false)}
              onCreated={(invite) => {
                setRevealedInvite(invite);
                refresh();
              }}
            />
          </Dialog>
        </div>
      </div>

      <InviteRevealDialog
        invite={revealedInvite}
        open={revealedInvite !== null}
        onOpenChange={(o) => { if (!o) setRevealedInvite(null); }}
      />

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      ) : loading ? (
        <LoadingState />
      ) : (
        <>
          <Section title="Active Members" count={filteredMembers.length} totalLabel={filteredMembers.length === members?.length ? null : `${members?.length ?? 0}`}>
            {filteredMembers.length === 0 ? (
              <Empty>{query ? "No members match your search." : "No members yet — invite someone to get started."}</Empty>
            ) : (
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map((m) => (
                      <MemberRow
                        key={m.userId}
                        member={m}
                        clientId={activeClientId!}
                        currentUserId={user?.userId}
                        isAdmin={!!isAdmin}
                        onChanged={refresh}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>

          {(filteredInvites.length > 0 || query === "") && (
            <Section title="Pending invites" count={filteredInvites.length}>
              {filteredInvites.length === 0 ? (
                <Empty>No pending invites.</Empty>
              ) : (
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvites.map((i) => (
                        <InviteRow
                          key={i.id}
                          invite={i}
                          clientId={activeClientId!}
                          isAdmin={!!isAdmin}
                          onChanged={refresh}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function MemberRow({
  member, clientId, currentUserId, isAdmin, onChanged,
}: {
  member: MemberResponse;
  clientId: string;
  currentUserId?: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const isYou = member.userId === currentUserId;

  async function changeRole(newRole: ClientRole) {
    setBusy(true);
    await api.PATCH("/v1/client/members/{userId}", {
      params: { path: { userId: member.userId } },
      body: { role: newRole },
    });
    setBusy(false);
    onChanged();
  }

  async function remove() {
    if (!confirm(`Remove ${member.email} from this client?`)) return;
    setBusy(true);
    await api.DELETE("/v1/client/members/{userId}", {
      params: { path: { userId: member.userId } },
    });
    setBusy(false);
    onChanged();
  }

  const canMutate = isAdmin && !(isYou && member.role === "Owner");

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-medium">{member.name ?? member.email}</span>
            {isYou && <Badge variant="outline" className="px-1.5 text-[10px]">You</Badge>}
          </div>
          <span className="text-xs text-muted-foreground">{member.email}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={roleStyles(member.role)}>{member.role}</Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{dateFmt.format(new Date(member.joinedAt))}</TableCell>
      <TableCell className="text-right">
        {canMutate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" disabled={busy}>
                {busy ? <Spinner className="size-3.5" /> : <MoreVertical className="size-3.5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ASSIGNABLE_ROLES.filter((r) => r !== member.role).map((r) => (
                <DropdownMenuItem key={r} onClick={() => changeRole(r)}>
                  Change to {r}
                </DropdownMenuItem>
              ))}
              {member.role !== "Owner" && (
                <DropdownMenuItem onClick={() => changeRole("Owner")}>
                  Promote to Owner
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={remove} className="text-destructive">
                <Trash2 className="size-3.5" /> {isYou ? "Leave client" : "Remove"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  );
}

function InviteRow({
  invite, clientId, isAdmin, onChanged,
}: {
  invite: ClientInviteResponse;
  clientId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  async function revoke() {
    if (!confirm(`Revoke invite for ${invite.email}?`)) return;
    setBusy(true);
    await api.DELETE("/v1/client/invites/{inviteId}", {
      params: { path: { inviteId: invite.id } },
    });
    setBusy(false);
    onChanged();
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Hourglass className="size-3.5 text-muted-foreground" />
          <span className="font-medium">{invite.email}</span>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={roleStyles(invite.role)}>{invite.role}</Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{dateFmt.format(new Date(invite.createdAt))}</TableCell>
      <TableCell className="text-muted-foreground">{dateFmt.format(new Date(invite.expiresAt))}</TableCell>
      <TableCell className="text-right">
        {isAdmin && (
          <Button variant="ghost" size="icon-sm" onClick={revoke} disabled={busy} className="text-destructive">
            {busy ? <Spinner className="size-3.5" /> : <Trash2 className="size-3.5" />}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function InviteDialog({
  clientId, onClose, onCreated,
}: {
  clientId: string;
  onClose: () => void;
  onCreated: (invite: ClientInviteWithTokenResponse) => void;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClientRole>("Developer");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }

    setSubmitting(true);
    const { data, error: apiError, response } = await api.POST("/v1/client/invites", {
      body: { email: email.trim().toLowerCase(), role },
    });
    setSubmitting(false);

    if (apiError || !data) {
      const detail = (apiError as { detail?: string } | undefined)?.detail;
      if (response?.status === 409) {
        setError(detail ?? "An invite for that email already exists, or that user is already a member.");
      } else {
        setError(detail ?? "Couldn't create invite.");
      }
      return;
    }

    onClose();
    onCreated(data);
  }

  return (
    <DialogContent className="max-w-md">
      <form onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>Invite a member</DialogTitle>
          <DialogDescription>
            They'll get a link they can open and accept with Google sign-in. The OAuth-verified email
            must match exactly.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-email">Work email</Label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="invite-email"
                type="email"
                placeholder="dev@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-8"
                autoFocus
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Label>Role</Label>
            <div className="flex flex-col gap-1.5" role="radiogroup" aria-label="Role">
              {ROLE_OPTIONS.map((opt) => {
                const selected = role === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setRole(opt.value)}
                    className={cn(
                      "flex items-center gap-3 rounded-full border px-3 py-1.5 text-left text-sm transition",
                      selected
                        ? "border-primary/60 bg-primary/15 text-foreground ring-2 ring-primary/40"
                        : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                        selected
                          ? "border-transparent bg-primary text-primary-foreground"
                          : "border-border bg-muted text-foreground"
                      )}
                    >
                      {opt.value}
                    </span>
                    <span className={cn("text-xs leading-snug", selected ? "text-foreground" : "")}>
                      {opt.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting && <Spinner className="size-4" />}
            Send invite
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Section({ title, count, totalLabel, children }: { title: string; count: number; totalLabel?: string | null; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {count}{totalLabel ? ` of ${totalLabel}` : ""}
        </span>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
      <Inbox className="size-6" />
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border py-12 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Loading members…
    </div>
  );
}
